import { makeAutoObservable, runInAction } from 'mobx'
import { bannerStore, EnumAlertBannerVariant } from 'shared/ui'
import { LINK_HOW_TO_GET_DESKTOP_BROWSER_NOTIFICATIONS } from 'shared/constants/links'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { NotificationsApi } from 'entities/Notifications/api/notifications'
import { urlBase64ToUint8Array } from 'entities/Notifications/ui/NotificationsBanner/lib/urlBase64ToUint8Array'

export class NotificationsBannerStore {
  private _banner_id = 'notifications_banner'
  private _initialized = false
  private _needsPermission = false

  constructor(readonly entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    await Promise.all([this.entities.usersStore.initPromise])

    this._initNotification()

    runInAction(() => {
      this._initialized = true
    })
  }

  private _initNotification = () => {
    if (!('Notification' in window)) return

    if (!this.entities.usersStore.user) {
      this._needsPermission = false
      return
    }

    if (Notification.permission === 'default') {
      this._needsPermission = true
    }

    if (Notification.permission === 'granted') {
      this._registerAndSubscribe()
    }
  }

  initBanner = () => {
    if (this.show) {
      bannerStore.add({
        id: this._banner_id,
        alert: {
          title:
            'Browser notifications are blocked. Give Salesmsg permission to send notifications to this browser.',
          variant: EnumAlertBannerVariant.Info,
          ariaLabel: 'notifications_banner',
        },
        action: {
          text: 'Enable',
          onAction: () => {
            this.onAction()
          },
        },
        secondAction: {
          text: 'Show me how',
          onAction: () => {
            this.onSecondAction()
          },
        },
        actionClose: {
          closeId: this._banner_id,
        },
      })
    } else {
      bannerStore.remove(this._banner_id)
    }
  }

  dispose = () => {
    bannerStore.remove(this._banner_id)
  }

  onAction = async () => {
    if (!('Notification' in window)) return

    try {
      const result = await Notification.requestPermission()

      if (result === 'granted') {
        await this._registerAndSubscribe()
      } else {
        await this._unsubscribeFromPush()
      }
    } catch (e) {
      console.error('Failed to request notification permission', e)
    }

    runInAction(() => {
      this._needsPermission = false
    })

    bannerStore.remove(this._banner_id)
  }

  onSecondAction = () => {
    window.open(LINK_HOW_TO_GET_DESKTOP_BROWSER_NOTIFICATIONS, '_blank')
  }

  get show() {
    return this._initialized && this._needsPermission
  }

  private _registerAndSubscribe = async () => {
    if (!('serviceWorker' in navigator)) return

    try {
      const registration = await navigator.serviceWorker.register('/sw-salesmessage.js')
      await navigator.serviceWorker.ready

      if (!registration.pushManager) return

      const existingSubscription = await registration.pushManager.getSubscription()

      if (existingSubscription) {
        await this._updatePushSubscription(existingSubscription)
        return
      }

      const vapidKey = window.Config?.vapid?.key

      if (!vapidKey) return

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      await this._updatePushSubscription(subscription)

      runInAction(() => {
        this._needsPermission = false
      })
    } catch (e) {
      logger.error(e)
    }
  }

  private _updatePushSubscription = async (subscription: PushSubscription) => {
    if (!this.entities.usersStore.user) return

    const p256dh = subscription.getKey('p256dh')
    const auth = subscription.getKey('auth')

    if (!p256dh || !auth) return

    const data = {
      endpoint: subscription.endpoint,
      public_key: btoa(String.fromCharCode(...new Uint8Array(p256dh))),
      auth_token: btoa(String.fromCharCode(...new Uint8Array(auth))),
    }

    try {
      await NotificationsApi.updateUsersPush(data)
    } catch (e) {
      logger.error(e)
    }
  }

  private _unsubscribeFromPush = async () => {
    if (!('serviceWorker' in navigator)) return
    if (!this.entities.usersStore.user) return

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        await subscription.unsubscribe()
        await NotificationsApi.deleteUsersPush({ endpoint: subscription.endpoint })
      }

      runInAction(() => {
        this._needsPermission = false
      })
    } catch (e) {
      logger.error(e)
    }
  }
}
